
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShoppingCart, RefreshCw, Gift as GiftIcon, Wallet } from 'lucide-react';
import { type Gift, type Brand, type MarketAction } from '@/utils/types';
import ActionButtons from './ActionButtons';
import MarketActionContent from '../market/MarketActionContent';

interface TransactionActionDialogProps {
  isOpen: boolean;
  selectedGift: Gift | null;
  targetGift: Gift | null;
  targetEmail: string;
  activeTab: MarketAction;
  brands: Brand[];
  isProcessing: boolean;
  onClose: () => void;
  onTabChange: (value: MarketAction) => void;
  onTargetGiftChange: (gift: Gift) => void;
  onTargetEmailChange: (email: string) => void;
  onAction: () => void;
  onShowQRCode: () => void;
}

const TransactionActionDialog: React.FC<TransactionActionDialogProps> = ({
  isOpen,
  selectedGift,
  targetGift,
  targetEmail,
  activeTab,
  brands,
  isProcessing,
  onClose,
  onTabChange,
  onTargetGiftChange,
  onTargetEmailChange,
  onAction,
  onShowQRCode
}) => {
  // Helper function to determine button text based on active tab and state
  const getButtonText = () => {
    if (isProcessing) return 'Processing...';
    
    if (activeTab === 'regift') {
      return !targetEmail ? "Select Recipient" : "Confirm Re-Gift";
    }
    
    if (activeTab === 'consume') {
      return "Use GivGo Bucks";
    }
    
    return `Confirm ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`;
  };

  // Helper function to determine if button should be disabled
  const isButtonDisabled = () => {
    if (!selectedGift || isProcessing) return true;
    if (activeTab === 'exchange' && !targetGift) return true;
    if (activeTab === 'regift' && !targetEmail) return true;
    return false;
  };

  // Handle action button tab change
  const handleActionTabChange = (tab: MarketAction) => {
    console.log("TransactionActionDialog: Changing tab to:", tab);
    onTabChange(tab);
  };

  // Handle the primary action button click
  const handleActionClick = () => {
    if (activeTab === 'regift' && !targetEmail) {
      return;
    }
    console.log("TransactionActionDialog: Handling action click for tab:", activeTab);
    onAction();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Transaction Options</DialogTitle>
          <DialogDescription>
            Choose what to do with your selected GivGo Bucks
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <ActionButtons activeAction={activeTab} onActionChange={handleActionTabChange} />
          
          <div className="mt-6 min-h-[250px]">
            {selectedGift && (
              <MarketActionContent 
                activeTab={activeTab}
                selectedGift={selectedGift}
                targetGift={targetGift}
                targetEmail={targetEmail}
                brands={brands}
                onTargetGiftChange={onTargetGiftChange}
                onTargetEmailChange={onTargetEmailChange}
                onAction={onAction}
                onShowQRCode={onShowQRCode}
              />
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button
            className="w-full bg-givgo-purple hover:bg-givgo-purple/90"
            disabled={isButtonDisabled()}
            onClick={handleActionClick}
          >
            {getButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionActionDialog;
